import { Avatar, IconGraphy, Table, Tooltip } from '@leapeasy/ui-kit';
import { Typography, Badge } from '@leapeasy/ui-kit';
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { getDemoData } from 'utils/helpers';
import styled from 'styled-components';
import { ListExportScopeModal } from '../shared/ListExportScopeModal';
import { GlobalUXState } from 'components/GlobalUXState';
import { fitContentTextColumnOptions } from 'utils/fitContentTextColumnOptions';
import { ROLE_DISPLAY_LABELS } from 'utils/entityTerminology';

const USER_ROLE = {
  admin: { text: ROLE_DISPLAY_LABELS.admin, badge: 'neutral' },
  ll: { text: ROLE_DISPLAY_LABELS.ll, badge: 'purple' },
  vp: { text: ROLE_DISPLAY_LABELS.vp, badge: 'parisGreen' },
  rm: { text: ROLE_DISPLAY_LABELS.rm, badge: 'tomato' },
  pm: { text: ROLE_DISPLAY_LABELS.pm, badge: 'sunglow' },
};

export const ActiveLogTable = forwardRef(
  (
    {
      activeLogs,
      filter,
      onChangeFilter,
      pagination,
      onChangePagination,
      sortOptions,
      onChangeSort,
      dropFilter,
      onClickDetails,
      onRequestExportData,
      onRequestFullExportData,
      exportFullDatasetTotal,
      dropFilterActiveCount,
      dropFilterActiveSummary,
    },
    ref,
  ) => {
    const [tableData, setTableData] = useState([]);
    const [exportModalOpen, setExportModalOpen] = useState(false);
    const tableRef = useRef(null);
    useImperativeHandle(ref, () => ({
      exportCsv: (...args) => tableRef.current?.exportCsv?.(...args),
    }));
    const isDemo = useSelector((state) => state.getIn(['ui', 'demo']));

    useEffect(() => {
      const data = [];
      if (activeLogs) {
        activeLogs.forEach((log, index) => {
          data.push([
            {
              image: isDemo ? null : log.image,
              is_online: log.is_online,
            },
            isDemo ? getDemoData('username') : log.username,
            log.id,
            isDemo ? getDemoData('email') : log.email,
            isDemo ? getDemoData('phone') : log.phone,
            log.job_title,
            log.role,
            log.activelogs?.length,
            log.id,
          ]);
        });
        setTableData(data);
      }
    }, [activeLogs, isDemo]);

    const buildActiveLogRow = (log, index) => [
      {
        image: isDemo ? null : log.image,
        is_online: log.is_online,
      },
      isDemo ? getDemoData('username') : log.username,
      log.id,
      isDemo ? getDemoData('email') : log.email,
      isDemo ? getDemoData('phone') : log.phone,
      log.job_title,
      log.role,
      log.activelogs?.length,
      log.id,
    ];

    return (
      <>
        <Table
          showEmptyState
          emptyContent={
            <GlobalUXState
              variant={filter?.searchText ? 'noResults' : 'noData'}
              compact
              description={
                filter?.searchText
                  ? 'No active log entries match your search.'
                  : 'No active log entries yet.'
              }
            />
          }
          ref={tableRef}
          onExportButtonClick={() => setExportModalOpen(true)}
          onRequestExportData={
            onRequestExportData
              ? async () => {
                  const rows = await onRequestExportData();
                  return (rows || []).map((log, index) => buildActiveLogRow(log, index));
                }
              : undefined
          }
          onRequestFullExportData={
            onRequestFullExportData
              ? async () => {
                  const rows = await onRequestFullExportData();
                  return (rows || []).map((log, index) => buildActiveLogRow(log, index));
                }
              : undefined
          }
          data={tableData}
          autoTooltipForDefaultCells
          filter={filter}
          dropFilter={dropFilter}
          dropFilterActiveCount={dropFilterActiveCount}
          dropFilterActiveSummary={dropFilterActiveSummary}
          onChangeFilter={onChangeFilter}
          onChangeRowsPerPage={(value) =>
            onChangePagination({
              ...pagination,
              rowsPerPage: value,
              pageNumber: 0,
            })
          }
          pagination={pagination}
          sortOptions={sortOptions}
          onChangeSort={onChangeSort}
          style={{ width: '100%' }}
          title="User Active Log"
          leastColumnCount={1}
          columnWidthStorageKey="activity-log"
          columns={[
            {
              name: 'Profile',
              options: {
                flex: '35px 1 1',
                customBodyRenderer: (value, _) => {
                  return (
                    <AvatarContainer is_online={!!value.is_online}>
                      <Avatar
                        size="medium"
                        iconImage={
                          value.image ? <img src={value.image} /> : <IconGraphy icon="Users.User" />
                        }
                      />
                      <span />
                    </AvatarContainer>
                  );
                },
              },
              key: 'image',
            },
            {
              name: 'Name',
              options: {
                ...fitContentTextColumnOptions(),
                sort: true,
                customBodyRenderer: (value) => (
                  <CustomTypography align="left" variant="body2" style={{ cursor: 'pointer' }}>
                    {value}
                  </CustomTypography>
                ),
              },
              key: 'username',
            },
            {
              name: 'ID',
              options: {
                flex: '50px 1 1',
                customBodyRenderer: (value, tableMeta) => (
                  <CustomTypography align="left" variant="body2" style={{ cursor: 'pointer' }}>
                    {value}
                  </CustomTypography>
                ),
                sort: true,
              },
              key: 'id',
            },
            {
              name: 'Email',
              options: {
                flex: '200px 1 1',
                customBodyRenderer: (value, tableMeta) => (
                  <CustomTypography align="left" variant="body2" style={{ cursor: 'pointer' }}>
                    {value}
                  </CustomTypography>
                ),
                sort: true,
              },
              key: 'email',
            },
            {
              name: 'Phone Number',
              options: {
                minWidth: 130,
                flex: '130px 1 1',
                customBodyRenderer: (value, tableMeta) => (
                  <CustomTypography align="left" variant="body2" style={{ cursor: 'pointer' }}>
                    {value}
                  </CustomTypography>
                ),
                sort: true,
              },
              key: 'phone',
            },
            {
              name: 'Job Title',
              options: {
                minWidth: 160,
                flex: '160px 1 1',
                sort: true,
                customBodyRenderer: (value, tableMeta) => (
                  <CustomTypography align="left" variant="body2" style={{ cursor: 'pointer' }}>
                    {value}
                  </CustomTypography>
                ),
              },
              key: 'job_title',
            },
            {
              name: 'User Role',
              options: {
                sort: true,
                flex: '100px 1 1',
                customBodyRenderer: (value, tableMeta) => (
                  <Badge
                    color={USER_ROLE[value].badge}
                    rounded
                    textSize="small"
                    label={USER_ROLE[value].text}
                  />
                ),
              },
              key: 'role',
            },
            {
              name: 'Active Count',
              options: {
                minWidth: 100,
                flex: '100px 0 0',
                shrink: false,
                align: 'center',
                sort: true,
                customBodyRenderer: (value) => (
                  <Typography align="center" variant="body1">
                    {Number(value ?? 0).toLocaleString('en-US')}
                  </Typography>
                ),
              },
              key: 'activelogs.length',
            },
            {
              name: 'Detail',
              options: {
                minWidth: 56,
                flex: '56px 0 0',
                shrink: false,
                excludeFromExport: true,
                customBodyRenderer: (_, row) => {
                  const userId = row?.[row.length - 1];
                  const user = activeLogs?.find((log) => `${log.id}` === `${userId}`);
                  return (
                    <Tooltip title="View activity details" placement="top" showCarot>
                      <DetailButton
                        type="button"
                        aria-label="View activity details"
                        onClick={(event) => {
                          event.stopPropagation();
                          if (user && onClickDetails) {
                            onClickDetails(user);
                          }
                        }}
                      >
                        <IconGraphy icon="FileFolder.Description" style={{ color: '#702572' }} />
                      </DetailButton>
                    </Tooltip>
                  );
                },
              },
              key: 'detail',
            },
          ]}
        />
        <ListExportScopeModal
          open={exportModalOpen}
          onClose={() => setExportModalOpen(false)}
          tableRef={tableRef}
          title="User Active Log"
          currentPageRowCount={tableData.length}
          paginationTotalItems={pagination.totalItems}
          fullDatasetTotal={exportFullDatasetTotal}
          enableFiltered={Boolean(onRequestExportData)}
          enableFull={Boolean(onRequestFullExportData)}
          enableSelected={false}
        />
      </>
    );
  },
);

ActiveLogTable.displayName = 'ActiveLogTable';

const CustomTypography = styled(Typography)`
  word-break: normal;
  overflow-wrap: break-word;
`;

const AvatarContainer = styled.div`
  position: relative;
  width: min-content;
  span {
    width: 8px;
    height: 8px;
    background-color: ${(props) => (props.is_online ? '#16B364' : '#9E9E9E')};
    position: absolute;
    border-radius: 50px;
    right: 1px;
    bottom: 1px;
  }
`;

const DetailButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;
